import type {
  ApiResponse,
  CreateWorkspaceInput,
  InvitationInfo,
  PublicUser,
  TokenPair,
  Workspace,
  WorkspaceInvitation,
  WorkspaceMember,
  WorkspaceRole,
} from '../types/index'

// Re-export workspace types so consumers can `import { type WorkspaceMember,
// ... } from '@creditify/shared-fe/api-client'` without reaching into the
// types module directly — pages like WorkspaceMembersPage rely on this.
export type {
  CreateWorkspaceInput,
  InvitationInfo,
  PublicUser,
  TokenPair,
  Workspace,
  WorkspaceInvitation,
  WorkspaceMember,
  WorkspaceRole,
}

// Single source of truth for the Creditify Go-gateway API. Every module's
// REST surface is reachable through the same fetch wrapper; auth bearer
// token is injected from a getter set at app boot.

const BASE_URL =
  // Vite injects import.meta.env on the SPA side; falls back to the
  // documented gateway prefix for SSR / node test environments.
  (typeof window !== 'undefined' && (window as { __API_BASE__?: string }).__API_BASE__) ||
  '/api/v1'

let _getToken: (() => string | null) | null = null

export function setAccessTokenGetter(g: () => string | null) {
  _getToken = g
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers)
  if (!headers.has('Content-Type') && init.body) {
    headers.set('Content-Type', 'application/json')
  }
  const token = _getToken?.()
  if (token) headers.set('Authorization', `Bearer ${token}`)

  const res = await fetch(`${BASE_URL}${path}`, {
    ...init,
    credentials: 'include',
    headers,
  })
  if (res.status === 204) return undefined as T
  const body = (await res.json().catch(() => ({}))) as ApiResponse<T>
  if (!res.ok || body.error) {
    const code = body.error?.code ?? 'UNKNOWN'
    const msg = body.error?.message ?? `request failed: ${res.status}`
    throw new Error(`[${code}] ${msg}`)
  }
  return body.data as T
}

export const authApi = {
  register(input: {
    email: string
    password: string
    displayName?: string
    locale?: string
  }): Promise<TokenPair> {
    return request<TokenPair>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(input),
    })
  },
  login(email: string, password: string): Promise<TokenPair> {
    return request<TokenPair>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    })
  },
  refresh(refreshToken: string): Promise<TokenPair> {
    return request<TokenPair>('/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ refreshToken }),
    })
  },
  logout(refreshToken: string): Promise<void> {
    return request<void>('/auth/logout', {
      method: 'POST',
      body: JSON.stringify({ refreshToken }),
    })
  },
  me(): Promise<PublicUser> {
    return request<PublicUser>('/auth/me')
  },
  updateMe(input: { displayName?: string; locale?: string }): Promise<PublicUser> {
    return request<PublicUser>('/auth/me', {
      method: 'PATCH',
      body: JSON.stringify(input),
    })
  },
  switchWorkspace(workspaceId: string): Promise<{ accessToken: string; expiresAt: string }> {
    return request<{ accessToken: string; expiresAt: string }>(
      '/auth/workspace/switch',
      { method: 'POST', body: JSON.stringify({ workspaceId }) },
    )
  },
  // GDPR Art. 20 — JSON dump of the auth module's slice of personal data.
  exportMe(): Promise<unknown> {
    return request<unknown>('/auth/me/export')
  },
  // GDPR Art. 17 — soft-delete + revoke every refresh token. Caller's
  // access token stays valid until expiry (≤15 min).
  deleteMe(): Promise<void> {
    return request<void>('/auth/me', { method: 'DELETE' })
  },
  // Sessions — every refresh-token row the caller owns, newest first.
  // Used by the settings page to render "where am I logged in".
  listSessions(): Promise<SessionRow[]> {
    return request<SessionRow[]>('/auth/me/sessions')
  },
  // Kick one session off — same shape as DELETE /me but scoped to a
  // single device. The auth store falls back to logout if the deleted
  // session id matches the caller's current refresh token.
  revokeSession(id: string): Promise<void> {
    return request<void>(`/auth/me/sessions/${id}`, { method: 'DELETE' })
  },
}

export interface SessionRow {
  id: string
  created_at: string
  expires_at: string
  revoked_at?: string | null
  user_agent?: string | null
  ip?: string | null
}

export interface AuditEntry {
  id: number
  actor_user_id: string
  actor_email?: string
  action: string
  target_kind: string
  target_id?: string | null
  meta: Record<string, unknown>
  ip?: string | null
  user_agent?: string | null
  created_at: string
}

export interface Suspension {
  workspace_id: string
  reason: string
  suspended_by: string
  suspended_at: string
}

export interface AuditFilter {
  actor?: string
  action?: string
  kind?: string
  target?: string
  since?: string
  until?: string
  limit?: number
  offset?: number
}

export interface BudgetPeriod {
  id: string
  workspace_id: string
  year: number
  month: number
  income_cents: number
  currency: string
  note?: string | null
  created_at: string
  updated_at: string
}

export interface BudgetLine {
  id: string
  period_id: string
  category: 'OBLI' | 'INVO' | 'FOOD' | 'OTHER' | 'SAVG' | 'BUSINESS'
  planned_cents: number
  actual_cents: number
  note?: string | null
  updated_at: string
}

export interface BankAccount {
  id: string
  workspace_id: string
  name: string
  iban?: string | null
  currency: string
  bank_bic?: string | null
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface Transaction {
  id: string
  workspace_id: string
  account_id: string
  import_id?: string | null
  direction: 'CRDT' | 'DBIT'
  amount_cents: number
  currency: string
  booked_at: string
  value_at?: string | null
  counterparty_name?: string | null
  counterparty_iban?: string | null
  remittance?: string | null
  bank_ref?: string | null
  raw: Record<string, unknown>
  created_at: string
}

export interface TransactionFilter {
  account_id?: string
  direction?: 'CRDT' | 'DBIT'
  from?: string
  to?: string
  q?: string
  limit?: number
  offset?: number
}

export const statementsApi = {
  listAccounts(): Promise<BankAccount[]> {
    return request<BankAccount[]>('/statements/accounts')
  },
  createAccount(input: {
    name: string
    iban?: string
    currency?: string
    bank_bic?: string
  }): Promise<BankAccount> {
    return request<BankAccount>('/statements/accounts', {
      method: 'POST',
      body: JSON.stringify(input),
    })
  },
  deactivateAccount(id: string): Promise<void> {
    return request<void>(`/statements/accounts/${id}`, { method: 'DELETE' })
  },
  accountBalances(): Promise<AccountBalance[]> {
    return request<AccountBalance[]>('/statements/accounts/balances')
  },
  listTransactions(f: TransactionFilter = {}): Promise<Transaction[]> {
    const qs = new URLSearchParams()
    for (const [k, v] of Object.entries(f)) {
      if (v !== undefined && v !== '') qs.set(k, String(v))
    }
    return request<Transaction[]>(
      `/statements/transactions${qs.toString() ? `?${qs.toString()}` : ''}`,
    )
  },
  createTransaction(input: {
    account_id: string
    direction: 'CRDT' | 'DBIT'
    amount_cents: number
    currency: string
    booked_at: string
    counterparty_name?: string
    remittance?: string
    bank_ref?: string
  }): Promise<Transaction> {
    return request<Transaction>('/statements/transactions', {
      method: 'POST',
      body: JSON.stringify(input),
    })
  },
  // Multipart upload — request() always JSON-stringifies, so go
  // through fetch directly. Reuses the same access-token getter the
  // module-level request() uses.
  async importFile(
    accountId: string,
    file: File,
  ): Promise<{ import_id: string; inserted: number; duplicates: number; errors: number }> {
    const path =
      file.name.toLowerCase().endsWith('.xml') ||
      file.type === 'text/xml' ||
      file.type === 'application/xml'
        ? '/statements/imports/camt053'
        : '/statements/imports/csv'
    const fd = new FormData()
    fd.append('account_id', accountId)
    fd.append('file', file)
    const token = _getToken?.()
    const r = await fetch(`${BASE_URL}${path}`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      body: fd,
      credentials: 'include',
    })
    const body = (await r.json().catch(() => ({}))) as ApiResponse<{
      import_id: string
      inserted: number
      duplicates: number
      errors: number
    }>
    if (!r.ok || body.error) {
      const code = body.error?.code ?? 'UNKNOWN'
      const msg = body.error?.message ?? `request failed: ${r.status}`
      throw new Error(`[${code}] ${msg}`)
    }
    return body.data!
  },

  /** @deprecated use importFile — routes by extension (.xml → camt053, else csv) */
  importCSV(
    accountId: string,
    file: File,
  ): Promise<{ import_id: string; inserted: number; duplicates: number; errors: number }> {
    return this.importFile(accountId, file)
  },
  // Auto-categorization rules.
  listRules(): Promise<TxRule[]> {
    return request<TxRule[]>('/statements/rules')
  },
  createRule(input: {
    match_field: TxRuleMatchField
    match_value: string
    set_category: string
    priority?: number
  }): Promise<TxRule> {
    return request<TxRule>('/statements/rules', {
      method: 'POST',
      body: JSON.stringify(input),
    })
  },
  deleteRule(id: string): Promise<void> {
    return request<void>(`/statements/rules/${id}`, { method: 'DELETE' })
  },
  backfillRules(): Promise<{ scanned: number; categorized: number }> {
    return request<{ scanned: number; categorized: number }>('/statements/rules/backfill', {
      method: 'POST',
    })
  },
  anomalies(filter: { lookback_days?: number; threshold?: number } = {}): Promise<TxAnomaly[]> {
    const qs = new URLSearchParams()
    for (const [k, v] of Object.entries(filter)) {
      if (v !== undefined) qs.set(k, String(v))
    }
    return request<TxAnomaly[]>(
      `/statements/anomalies${qs.toString() ? `?${qs.toString()}` : ''}`,
    )
  },
  yearlyExport(year: number): Promise<YearlyCategoryRow[]> {
    return request<YearlyCategoryRow[]>(
      `/statements/exports/yearly?year=${encodeURIComponent(year)}`,
    )
  },
  // Download the full filtered transaction list as a CSV blob.
  // Mirrors the yearlyExportCSV pattern — bearer attached manually so
  // the gateway authenticates the download path the same way it
  // authenticates the rest of /api/v1.
  async transactionsExportCSV(filter: {
    account_id?: string
    direction?: 'CRDT' | 'DBIT' | ''
    from?: string
    to?: string
    q?: string
  } = {}): Promise<void> {
    const qs = new URLSearchParams({ format: 'csv' })
    for (const [k, v] of Object.entries(filter)) {
      if (v !== undefined && v !== '') qs.set(k, String(v))
    }
    const token = _getToken?.()
    const r = await fetch(
      `${BASE_URL}/statements/exports/transactions?${qs.toString()}`,
      {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        credentials: 'include',
      },
    )
    if (!r.ok) {
      throw new Error(`[HTTP_${r.status}] transactions export failed`)
    }
    const blob = await r.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'creditify-transactions.csv'
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  },
  // CSV download — open a blob URL in the same tab so the browser
  // saves the file. We attach the bearer manually because the gateway
  // wants Authorization on this path too.
  async yearlyExportCSV(year: number): Promise<void> {
    const token = _getToken?.()
    const r = await fetch(
      `${BASE_URL}/statements/exports/yearly?format=csv&year=${encodeURIComponent(year)}`,
      {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        credentials: 'include',
      },
    )
    if (!r.ok) {
      const code = `HTTP_${r.status}`
      throw new Error(`[${code}] yearly export failed`)
    }
    const blob = await r.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `creditify-yearly-${year}.csv`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  },
}

export interface YearlyCategoryRow {
  category: string
  direction: 'CRDT' | 'DBIT'
  currency: string
  tx_count: number
  total_cents: number
}

export interface TxAnomaly {
  tx_id: string
  workspace_id: string
  account_id: string
  booked_at: string
  direction: 'CRDT' | 'DBIT'
  amount_cents: number
  currency: string
  counterparty_name?: string | null
  remittance?: string | null
  category?: string | null
  mean_cents: number
  stddev_cents: number
  z_score: number
  abs_z: number
  sample_size: number
}

export type TxRuleMatchField = 'counterparty' | 'remittance' | 'iban' | 'amount_eq'

export interface TxRule {
  id: string
  workspace_id: string
  match_field: TxRuleMatchField
  match_value: string
  set_category: string
  priority: number
  is_active: boolean
  created_at: string
  updated_at: string
}

export type BillCategory = 'UTILITY' | 'SUBSCRIPTION' | 'INSURANCE' | 'LOAN' | 'OTHER'
export type BillStatus = 'UPCOMING' | 'OVERDUE' | 'PAID' | 'CANCELED'

export interface Bill {
  id: string
  workspace_id: string
  name: string
  category: BillCategory
  issuer?: string | null
  amount_cents: number
  currency: string
  due_date?: string | null
  paid_at?: string | null
  status: BillStatus
  source_kind?: string | null
  source_id?: string | null
  note?: string | null
  extra: Record<string, unknown>
  recurrence_cadence?: 'monthly' | 'quarterly' | 'yearly' | null
  recurrence_parent_id?: string | null
  created_at: string
  updated_at: string
}

export interface BillFilter {
  status?: BillStatus
  category?: BillCategory
  due_before?: string
  limit?: number
  offset?: number
}

export const billsApi = {
  list(f: BillFilter = {}): Promise<Bill[]> {
    const qs = new URLSearchParams()
    for (const [k, v] of Object.entries(f)) {
      if (v !== undefined && v !== '') qs.set(k, String(v))
    }
    return request<Bill[]>(`/bills${qs.toString() ? `?${qs.toString()}` : ''}`)
  },
  get(id: string): Promise<Bill> {
    return request<Bill>(`/bills/${id}`)
  },
  create(input: {
    name: string
    category: BillCategory
    issuer?: string
    amount_cents: number
    currency?: string
    due_date?: string
    note?: string
    recurrence_cadence?: '' | 'monthly' | 'quarterly' | 'yearly'
  }): Promise<Bill> {
    return request<Bill>('/bills', { method: 'POST', body: JSON.stringify(input) })
  },
  markPaid(id: string): Promise<void> {
    return request<void>(`/bills/${id}/paid`, { method: 'POST' })
  },
  cancel(id: string): Promise<void> {
    return request<void>(`/bills/${id}/cancel`, { method: 'POST' })
  },
  // Per-bill line items: utility breakdown (kWh, m³, tariffs).
  listLineItems(billId: string): Promise<BillLineItem[]> {
    return request<BillLineItem[]>(`/bills/${billId}/line-items`)
  },
  createLineItem(
    billId: string,
    input: {
      description: string
      qty?: string
      unit?: string
      unit_price_cents?: string
      amount_cents: number
      vat_pct?: string
      period_start?: string
      period_end?: string
      meter_start?: string
      meter_end?: string
    },
  ): Promise<BillLineItem> {
    return request<BillLineItem>(`/bills/${billId}/line-items`, {
      method: 'POST',
      body: JSON.stringify(input),
    })
  },
  deleteLineItem(itemId: string): Promise<void> {
    return request<void>(`/bills/line-items/${itemId}`, { method: 'DELETE' })
  },
  lineItemTrends(unit: string, months = 24): Promise<BillLineItemTrendPoint[]> {
    const qs = new URLSearchParams({ unit, months: String(months) })
    return request<BillLineItemTrendPoint[]>(`/bills/line-items/trends?${qs.toString()}`)
  },
  payoff(
    billId: string,
    monthlyCents: number,
    aprPct: number,
  ): Promise<BillPayoffResult> {
    const qs = new URLSearchParams({
      monthly_cents: String(monthlyCents),
      apr_pct: String(aprPct),
    })
    return request<BillPayoffResult>(`/bills/${billId}/payoff?${qs.toString()}`)
  },
}

export interface BillPayoffPoint {
  month: number
  starting_balance_cents: number
  interest_cents: number
  principal_cents: number
  ending_balance_cents: number
}

export interface BillPayoffResult {
  principal_cents: number
  monthly_cents: number
  apr_pct: number
  months: number
  total_interest_cents: number
  schedule: BillPayoffPoint[]
}

// Monthly bucket from /bills/line-items/trends. qty rides as a string
// to keep the 4-decimal precision Latvian utility tariffs use.
export interface BillLineItemTrendPoint {
  month: string // YYYY-MM
  qty?: string | null
  amount_cents: number
  count: number
}

// Numeric columns ride as strings to keep the 4-6 decimals utility
// tariffs need. Convert with Number() at presentation time only.
export interface BillLineItem {
  id: string
  bill_id: string
  workspace_id: string
  description: string
  qty?: string | null
  unit?: string | null
  unit_price_cents?: string | null
  amount_cents: number
  vat_pct?: string | null
  period_start?: string | null
  period_end?: string | null
  meter_start?: string | null
  meter_end?: string | null
  created_at: string
}

export const budgetApi = {
  listPeriods(): Promise<BudgetPeriod[]> {
    return request<BudgetPeriod[]>('/budget/periods')
  },
  getPeriod(id: string): Promise<{ period: BudgetPeriod; lines: BudgetLine[] }> {
    return request<{ period: BudgetPeriod; lines: BudgetLine[] }>(`/budget/periods/${id}`)
  },
  createPeriod(input: {
    year: number
    month: number
    income_cents: number
    currency?: string
    note?: string
  }): Promise<BudgetPeriod> {
    return request<BudgetPeriod>('/budget/periods', {
      method: 'POST',
      body: JSON.stringify(input),
    })
  },
  upsertLine(
    periodId: string,
    category: BudgetLine['category'],
    input: { planned_cents: number; actual_cents: number; note?: string },
  ): Promise<BudgetLine> {
    return request<BudgetLine>(`/budget/periods/${periodId}/lines/${category}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    })
  },
  summary(): Promise<{
    period_count: number
    latest_year?: number
    latest_month?: number
    latest_income_cents: number
    latest_planned_cents: number
    latest_actual_cents: number
  }> {
    return request('/budget/summary')
  },
}

export interface BillsSummary {
  upcoming_count: number
  overdue_count: number
  upcoming_amount_cents: number
  overdue_amount_cents: number
  paid_last_30_count: number
  paid_last_30_amount_cents: number
}

export interface StatementsSummary {
  account_count: number
  transaction_count: number
  last_30_days_credits_cents: number
  last_30_days_debits_cents: number
  // Import-based balance: SUM(CRDT) − SUM(DBIT) across active
  // bank accounts. Best-effort estimate — only what's been imported
  // is counted.
  liquid_balance_cents: number
  latest_booked_at?: string
}

export interface AccountBalance {
  account_id: string
  balance_cents: number
  currency: string
}

export const summaryApi = {
  bills: (): Promise<BillsSummary> => request<BillsSummary>('/bills/summary'),
  statements: (): Promise<StatementsSummary> =>
    request<StatementsSummary>('/statements/summary'),
  savings: (): Promise<SavingsSummary> => request<SavingsSummary>('/savings/summary'),
  investments: (): Promise<InvestmentsSummary> =>
    request<InvestmentsSummary>('/investments/summary'),
}

// Investments — workspace portfolio.
export interface InvestmentAccount {
  id: string
  workspace_id: string
  name: string
  broker?: string | null
  currency: string
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface InvestmentPosition {
  id: string
  account_id: string
  workspace_id: string
  symbol: string
  issuer?: string | null
  currency: string
  qty: string
  avg_cost: string
  last_price?: string | null
  last_price_at?: string | null
  created_at: string
  updated_at: string
}

export interface InvestmentTrade {
  id: string
  account_id: string
  workspace_id: string
  symbol: string
  side: 'BUY' | 'SELL'
  qty: string
  price: string
  fees_cents: number
  currency: string
  traded_at: string
  note?: string | null
  created_at: string
}

export interface InvestmentsSummary {
  position_count: number
  cost_cents: number
  market_cents: number
  pnl_cents: number
}

export const investmentsApi = {
  listAccounts(): Promise<InvestmentAccount[]> {
    return request<InvestmentAccount[]>('/investments/accounts')
  },
  createAccount(input: {
    name: string
    broker?: string
    currency?: string
  }): Promise<InvestmentAccount> {
    return request<InvestmentAccount>('/investments/accounts', {
      method: 'POST',
      body: JSON.stringify(input),
    })
  },
  deactivateAccount(id: string): Promise<void> {
    return request<void>(`/investments/accounts/${id}`, { method: 'DELETE' })
  },
  listPositions(accountId?: string): Promise<InvestmentPosition[]> {
    const qs = accountId ? `?account_id=${encodeURIComponent(accountId)}` : ''
    return request<InvestmentPosition[]>(`/investments/positions${qs}`)
  },
  setLastPrice(positionId: string, price: string): Promise<void> {
    return request<void>(`/investments/positions/${positionId}/price`, {
      method: 'POST',
      body: JSON.stringify({ price }),
    })
  },
  listTrades(filter: {
    account_id?: string
    symbol?: string
    limit?: number
  } = {}): Promise<InvestmentTrade[]> {
    const qs = new URLSearchParams()
    for (const [k, v] of Object.entries(filter)) {
      if (v !== undefined && v !== '') qs.set(k, String(v))
    }
    return request<InvestmentTrade[]>(
      `/investments/trades${qs.toString() ? `?${qs.toString()}` : ''}`,
    )
  },
  createTrade(input: {
    account_id: string
    symbol: string
    side: 'BUY' | 'SELL'
    qty: string
    price: string
    fees_cents?: number
    currency?: string
    traded_at?: string
    note?: string
  }): Promise<InvestmentTrade> {
    return request<InvestmentTrade>('/investments/trades', {
      method: 'POST',
      body: JSON.stringify(input),
    })
  },
}

export interface SavingsGoal {
  id: string
  workspace_id: string
  name: string
  target_cents: number
  current_cents: number
  currency: string
  target_date?: string | null
  color?: string | null
  icon?: string | null
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface SavingsContribution {
  id: string
  goal_id: string
  workspace_id: string
  amount_cents: number
  contributed_at: string
  note?: string | null
  created_at: string
}

export interface SavingsSummary {
  goal_count: number
  total_target_cents: number
  total_saved_cents: number
}

export const savingsApi = {
  listGoals(includeInactive = false): Promise<SavingsGoal[]> {
    const qs = includeInactive ? '?include_inactive=true' : ''
    return request<SavingsGoal[]>(`/savings/goals${qs}`)
  },
  createGoal(input: {
    name: string
    target_cents: number
    currency?: string
    target_date?: string
    color?: string
    icon?: string
  }): Promise<SavingsGoal> {
    return request<SavingsGoal>('/savings/goals', {
      method: 'POST',
      body: JSON.stringify(input),
    })
  },
  deactivateGoal(id: string): Promise<void> {
    return request<void>(`/savings/goals/${id}`, { method: 'DELETE' })
  },
  listContributions(goalId: string, limit = 50): Promise<SavingsContribution[]> {
    return request<SavingsContribution[]>(
      `/savings/goals/${goalId}/contributions?limit=${limit}`,
    )
  },
  addContribution(
    goalId: string,
    input: { amount_cents: number; contributed_at?: string; note?: string },
  ): Promise<SavingsContribution> {
    return request<SavingsContribution>(`/savings/goals/${goalId}/contributions`, {
      method: 'POST',
      body: JSON.stringify(input),
    })
  },
  deleteContribution(id: string): Promise<void> {
    return request<void>(`/savings/contributions/${id}`, { method: 'DELETE' })
  },
}

export const adminApi = {
  audit(f: AuditFilter = {}): Promise<AuditEntry[]> {
    const qs = new URLSearchParams()
    for (const [k, v] of Object.entries(f)) {
      if (v !== undefined && v !== '') qs.set(k, String(v))
    }
    const tail = qs.toString() ? `?${qs.toString()}` : ''
    return request<AuditEntry[]>(`/admin/audit${tail}`)
  },
  suspensions(): Promise<Suspension[]> {
    return request<Suspension[]>('/admin/suspensions')
  },
  suspend(workspaceId: string, reason: string): Promise<void> {
    return request<void>('/admin/suspensions', {
      method: 'POST',
      body: JSON.stringify({ workspace_id: workspaceId, reason }),
    })
  },
  unsuspend(workspaceId: string): Promise<void> {
    return request<void>(`/admin/suspensions/${workspaceId}`, { method: 'DELETE' })
  },
  tenants(limit = 100, offset = 0): Promise<AdminTenant[]> {
    return request<AdminTenant[]>(
      `/admin/tenants?limit=${limit}&offset=${offset}`,
    )
  },
  stats(): Promise<AdminPlatformStats> {
    return request<AdminPlatformStats>('/admin/stats')
  },
}

export interface AdminPlatformStats {
  user_count: number
  workspaces_personal: number
  workspaces_business: number
  workspaces_suspended: number
  signups_last_24h: number
  audit_entries_last_24h: number
  active_workspaces_last_7d: number
}

export interface AdminTenant {
  id: string
  name: string
  kind: 'personal' | 'business'
  owner_user_id: string
  owner_email: string
  member_count: number
  is_suspended: boolean
  suspension_note?: string
  created_at: string
}

export const workspacesApi = {
  list(): Promise<Workspace[]> {
    return request<Workspace[]>('/workspaces')
  },
  get(id: string): Promise<Workspace> {
    return request<Workspace>(`/workspaces/${id}`)
  },
  create(input: CreateWorkspaceInput): Promise<Workspace> {
    return request<Workspace>('/workspaces', {
      method: 'POST',
      body: JSON.stringify(input),
    })
  },
  update(id: string, patch: Partial<CreateWorkspaceInput>): Promise<Workspace> {
    return request<Workspace>(`/workspaces/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    })
  },
  members(id: string): Promise<WorkspaceMember[]> {
    return request<WorkspaceMember[]>(`/workspaces/${id}/members`)
  },
  updateMemberRole(id: string, userId: string, role: WorkspaceRole): Promise<void> {
    return request<void>(`/workspaces/${id}/members/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    })
  },
  removeMember(id: string, userId: string): Promise<void> {
    return request<void>(`/workspaces/${id}/members/${userId}`, {
      method: 'DELETE',
    })
  },
  invitations(id: string): Promise<WorkspaceInvitation[]> {
    return request<WorkspaceInvitation[]>(`/workspaces/${id}/invitations`)
  },
  invite(id: string, email: string, role: WorkspaceRole = 'member'): Promise<{
    id: string
    token: string
    expiresAt: string
  }> {
    return request(
      `/workspaces/${id}/invitations`,
      { method: 'POST', body: JSON.stringify({ email, role }) },
    )
  },
  revokeInvitation(id: string, invitationId: string): Promise<void> {
    return request<void>(`/workspaces/${id}/invitations/${invitationId}`, {
      method: 'DELETE',
    })
  },
  invitationInfo(token: string): Promise<InvitationInfo> {
    return request<InvitationInfo>(`/invitations/${token}/info`)
  },
  acceptInvitation(token: string): Promise<Workspace> {
    return request<Workspace>('/invitations/accept', {
      method: 'POST',
      body: JSON.stringify({ token }),
    })
  },
}
